import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import '@theme/variant-picker';
import '@theme/media-gallery';

function renderQuickAddContent(productGrid, title, productUrl, quantity, variantUpdate = false) {
  title ||= productGrid.querySelector('.view-product-title a')?.textContent.trim() || '';
  const content = document.getElementById('quick-add-modal-content');
  if (!content) return;
  const previousScroll = variantUpdate ? {
    content: content.scrollTop,
    details: content.querySelector('.product-details')?.scrollTop || 0,
    media: content.querySelector('.product-information__media-column')?.scrollTop || 0,
  } : null;
  if (isMobileBreakpoint()) {
    const details = productGrid.querySelector('.product-details');
    const form = productGrid.querySelector('product-form-component');
    const picker = productGrid.querySelector('variant-picker');
    const price = productGrid.querySelector('product-price');
    const header = document.createElement('div');
    header.className = 'product-header';
    const link = document.createElement('a');
    link.textContent = title;
    link.href = productGrid.querySelector('.view-product-title a')?.href || productUrl;
    header.append(link);
    if (price) header.append(price);
    productGrid.append(header);
    if (picker) productGrid.append(picker);
    if (form) productGrid.append(form);
    details?.remove();
  }
  const ids = new Map();
  productGrid.querySelectorAll('form[id], input[id]').forEach((element) => {
    const id = element.getAttribute('id');
    ids.set(id, `quick-add-${id}`);
    element.setAttribute('id', ids.get(id));
  });
  productGrid.querySelectorAll('[form], label[for]').forEach((element) => {
    for (const attr of ['form', 'for']) {
      const id = element.getAttribute(attr);
      if (ids.has(id)) element.setAttribute(attr, ids.get(id));
    }
  });
  const selectedScript = productGrid.querySelector('variant-picker script[type="application/json"]');
  let mediaId;
  if (selectedScript?.textContent) {
    const selected = JSON.parse(selectedScript.textContent);
    mediaId = selected?.featured_media?.id;
  }
  productGrid.querySelectorAll('media-gallery').forEach((gallery) => {
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const selected = slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId)) || slides[0];
    slides.forEach((slide) => {
      const active = slide === selected;
      slide.style.setProperty('display', active ? 'block' : 'none', 'important');
      slide.setAttribute('aria-hidden', String(!active));
    });
  });
  if (variantUpdate) {
    productGrid.querySelectorAll('.product-details, .product-information__media-column, slideshow-slide').forEach((element) => {
      element.style.setProperty('animation', 'none', 'important');
    });
  }
  content.className = `${productGrid.className} quick-add-modal__content`;
  content.replaceChildren(...productGrid.childNodes);
  if (previousScroll) {
    content.scrollTop = previousScroll.content;
    const details = content.querySelector('.product-details');
    const media = content.querySelector('.product-information__media-column');
    if (details) details.scrollTop = previousScroll.details;
    if (media) media.scrollTop = previousScroll.media;
  }
  const selector = content.querySelector('quantity-selector-component');
  const input = selector?.querySelector('input[name="quantity"]');
  if (input && quantity !== undefined) {
    input.value = String(quantity);
    selector.updateConstraints?.(input.min || '1', input.max || null, input.step || '1');
  }
}

export class QuickAddComponent extends Component {
  static #openRequest = 0;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();


  static #stylesheetPromises = new Map();

  async #loadProductStyles(html) {
    const currentStyles = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    const themeStyles = currentStyles.find((link) => link.href.includes('/compiled_assets/styles.css'));
    if (!themeStyles) return;

    const themeStylesUrl = new URL(themeStyles.href);
    const styles = Array.from(html.querySelectorAll('link[rel="stylesheet"]')).filter((link) => {
      const url = new URL(link.href, location.href);
      return url.origin === themeStylesUrl.origin && url.pathname === themeStylesUrl.pathname;
    });

    await Promise.all(styles.map((source) => {
      const href = new URL(source.href, location.href).href;
      const pending = QuickAddComponent.#stylesheetPromises.get(href);
      if (pending) return pending;
      if (currentStyles.some((link) => link.href === href)) return Promise.resolve();

      const promise = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        const timeout = setTimeout(() => link.onerror(), 15000);
        link.onload = () => { clearTimeout(timeout); resolve(); };
        link.onerror = () => {
          clearTimeout(timeout);
          link.remove();
          QuickAddComponent.#stylesheetPromises.delete(href);
          reject(new Error('Failed to load quick-add product styles'));
        };
        themeStyles.before(link);
      });
      QuickAddComponent.#stylesheetPromises.set(href, promise);
      return promise;
    }));
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const request = ++QuickAddComponent.#openRequest;
    const button = event.target.closest('button');
    const currentUrl = this.productPageUrl;
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog) dialog.returnFocus = button;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    dialog?.cancelVariantUpdate();
    try {
      let grid = this.#cachedContent.get(currentUrl);
      if (!grid) {
        const html = await this.fetchProductPage(currentUrl);
        if (!html || request !== QuickAddComponent.#openRequest) return;
        await this.#loadProductStyles(html);
        const source = html.querySelector('[data-product-grid-content]');
        if (!source) throw new Error('Missing quick-add product content');
        grid = source.cloneNode(true);
        this.#cachedContent.set(currentUrl, grid);
      }
      if (request !== QuickAddComponent.#openRequest) return;
      await this.updateQuickAddModal(grid.cloneNode(true));
      this.#openQuickAddModal();
    } catch (error) {
      if (request !== QuickAddComponent.#openRequest || error.name === 'AbortError') return;
      dialog?.showLoadError(this.dataset.productTitle || '', currentUrl);
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const isCustomCardQuickAdd = this.classList.contains('cc-card__quick-add');
    dialogComponent.toggleAttribute('data-cc-card-quick-add', isCustomCardQuickAdd);
    if (isCustomCardQuickAdd) {
      dialogComponent.dataset.ccProductOptionsCount = this.dataset.productOptionsCount || '1';
    } else {
      delete dialogComponent.dataset.ccProductOptionsCount;
    }
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: AbortSignal.any([this.#abortController.signal, AbortSignal.timeout(15000)]),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    renderQuickAddContent(productGrid, this.dataset.productTitle || '', this.productPageUrl);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantSelected, this.#keepVariantSelectionLocal, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('change', this.#syncSelectedVariant, { capture: true, signal: this.#abortController.signal });
    this.addEventListener('submit', this.#guardPurchase, { capture: true, signal: this.#abortController.signal });
    this.addEventListener('click', this.#guardPurchase, { capture: true, signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * The dialog synchronizes its selected variant immediately, so the product form must not wait on a stale collection-view render.
   * @param {Event} event
   */
  #keepVariantSelectionLocal = (event) => {
    if (!this.hasAttribute('data-cc-card-quick-add')) return;
    event.stopImmediatePropagation();
  };

  #variantRequest = 0;
  #variantFetch;
  #variantPending = false;

  cancelVariantUpdate() {
    this.#variantRequest++;
    this.#variantFetch?.abort();
    this.#variantPending = false;
    this.removeAttribute('aria-busy');
  }

  #setPurchaseBlocked() {
    this.#variantPending = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('product-form-component button[type="submit"], .accelerated-checkout-block button, quantity-selector-component button, quantity-selector-component input').forEach((button) => {
      button.disabled = true;
    });
    this.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
      input.disabled = true;
    });
    this.querySelectorAll('.accelerated-checkout-block').forEach((block) => { block.inert = true; });
  }

  #showStatus(message) {
    let status = this.querySelector('[data-quick-add-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.quickAddStatus = '';
      status.setAttribute('role', 'alert');
      const host = this.querySelector('.buy-buttons-block') || this.querySelector('#quick-add-modal-content');
      host?.prepend(status);
    }
    status.textContent = message;
  }

  showLoadError(title, url) {
    const content = this.querySelector('#quick-add-modal-content');
    content.className = 'quick-add-modal__content';
    content.replaceChildren();
    const link = document.createElement('a');
    link.textContent = title;
    link.href = url;
    content.append(link);
    this.#showStatus(this.dataset.errorLabel || Theme.translations.recipient_form_error);
    this.showDialog();
  }

  #guardPurchase = (event) => {
    if (!this.#variantPending) return;
    if (event.type === 'submit' || event.target.closest('button[type="submit"], .accelerated-checkout-block')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #syncSelectedVariant = async (event) => {
    if (!this.hasAttribute('data-cc-card-quick-add')) return;
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    const picker = control.closest('variant-picker');
    if (!picker) return;
    event.stopImmediatePropagation();
    const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
    const controls = Array.from(picker.querySelectorAll('[data-option-position]')).sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
    const selected = controls.map((field) => field instanceof HTMLFieldSetElement
      ? field.querySelector('input:checked') : field instanceof HTMLSelectElement ? field.selectedOptions[0] : field);
    const values = selected.map((input) => input?.value || input?.dataset.optionValue || '');
    controls.forEach((field, index) => {
      const label = field.querySelector('.variant-option__swatch-value');
      if (label) label.textContent = values[index];
      field.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.dataset.currentChecked = String(input.checked);
      });
    });
    const optionIds = selected.map((input) => input?.dataset.optionValueId).filter(Boolean);
    const request = ++this.#variantRequest;
    this.#variantFetch?.abort();
    const controller = new AbortController();
    this.#variantFetch = controller;
    const quantity = this.querySelector('product-form-component input[name="quantity"]')?.value;
    this.#setPurchaseBlocked();
    this.querySelector('[data-quick-add-status]')?.remove();
    this.querySelectorAll('[data-quick-add-progress]').forEach((node) => node.remove());
    const progress = document.createElement('progress');
    progress.dataset.quickAddProgress = '';
    progress.setAttribute('aria-label', values.join(' / '));
    if (!picker.style.position) picker.style.position = 'relative';
    progress.style.cssText = 'position:absolute;inset-inline-start:0;bottom:0;width:5rem;height:3px;pointer-events:none';
    picker.append(progress);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const url = new URL(option.dataset.connectedProductUrl || picker.dataset.productUrl, location.href);
      url.searchParams.delete('variant');
      url.searchParams.delete('section_id');
      if (optionIds.length === values.length) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        const combinations = picker.querySelector('script[data-cc-variant-combinations]');
        const match = combinations && JSON.parse(combinations.textContent).find((variant) =>
          variant.options.length === values.length && variant.options.every((value, i) => value === values[i])
        );
        if (!match) {
          this.#showStatus(this.dataset.unavailableLabel || Theme.translations.recipient_form_error);
          return;
        }
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', match.id);
      }
      this.querySelectorAll('.view-product-title a, .product-header a').forEach((link) => { link.href = url.toString(); });
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Quick-add variant response failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request !== this.#variantRequest) return;
      const grid = html.querySelector('[data-product-grid-content]');
      const script = grid?.querySelector('variant-picker script[type="application/json"]');
      if (!grid || !script?.textContent) throw new Error('Incomplete quick-add variant response');
      const variant = JSON.parse(script.textContent);
      const exact = variant?.options?.length === values.length && variant.options.every((value, i) => value === values[i]);
      if (!exact) {
        this.#showStatus(this.dataset.unavailableLabel || Theme.translations.recipient_form_error);
        return;
      }
      const titleLink = grid.querySelector('.view-product-title a');
      renderQuickAddContent(grid, titleLink?.textContent.trim() || '', titleLink?.href || url.toString(), quantity, true);
      this.#variantPending = false;
      this.removeAttribute('aria-busy');
      if (!variant.available) {
        this.#setPurchaseBlocked();
        this.removeAttribute('aria-busy');
        this.#showStatus(this.dataset.unavailableLabel || Theme.translations.recipient_form_error);
      }
      const focusOption = Array.from(this.querySelectorAll('variant-picker [data-option-value-id]')).find(
        (input) => input.dataset.optionValueId === option.dataset.optionValueId
      );
      (focusOption?.closest('select') || focusOption)?.focus({ preventScroll: true });
    } catch (error) {
      if (request !== this.#variantRequest) return;
      this.#showStatus(this.dataset.errorLabel || Theme.translations.recipient_form_error);
    } finally {
      clearTimeout(timeout);
      if (request === this.#variantRequest) {
        this.removeAttribute('aria-busy');
        this.querySelectorAll('[data-quick-add-progress]').forEach((node) => node.remove());
      }
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.cancelVariantUpdate();
    this.returnFocus?.focus({ preventScroll: true });
    this.removeAttribute('data-cc-card-quick-add');
    delete this.dataset.ccProductOptionsCount;
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

